import { plainInstanceFootprint, readInstanceFootprint } from "../document/footprint-cells.ts";
import { MAX_TILE_ID } from "../document/packed-tile.ts";
import type { PropBehaviour, TileRef } from "../document/types.ts";
import { asArray, asBoolean, asInteger, asIntegerInRange, asJsonRecord, asRecord, asString, sortedRecord } from "../document/validate.ts";
import { TEMPLATE_ROLES, templateNameProblem, type PropTemplate, type TemplatePiece, type TemplateRole } from "./templates.ts";

/**
 * The template file (ADR-066 decision 4): one template per file, plain JSON with a schema version, written
 * with a fixed key order and sorted maps so equal templates are equal bytes, and read back with every value
 * checked, the first problem named by its path. A behaviour is read by the rules a document reads one by
 * (ADR-053, ADR-058), except that its frames are checked when the template is placed, against the world it
 * goes into, since a template belongs to no world.
 */

/**
 * The layout this build writes. It reads this and schema 1, whose pieces carry their sort offset in whole
 * rows as `sortOffset` where schema 2 carries whole pixels as `sortPixels` (ADR-098 decision 3).
 */
export const TEMPLATE_SCHEMA_VERSION = 3;

/**
 * The schema whose pieces carry whole rows.
 */
const ROWS_SCHEMA_VERSION = 1;

/**
 * The schemas this build reads: schema 1, whose pieces carry whole rows; schema 2, whole pixels (ADR-098); and
 * its own, schema 3, which adds a piece's own footprint (ADR-040 amendment of 2026-10-04), a field an older
 * build must not drop when it rewrites a file it read.
 */
const READABLE_TEMPLATE_SCHEMAS: readonly number[] = [ROWS_SCHEMA_VERSION, 2, TEMPLATE_SCHEMA_VERSION];

/**
 * The most pieces a template holds.
 */
export const MAX_TEMPLATE_PIECES = 256;

/**
 * The farthest a piece may sit from the anchor, in pixels either way: a 2048 tile map of 32 pixel tiles.
 */
const MAX_OFFSET = 65_536;

/**
 * The most activation cells a piece's behaviour holds, as a footprint override may.
 */
const MAX_CELLS = 4096;

const FORBIDDEN_KEY = "__proto__";

/**
 * Writes a template. Every array of two integers is written on one line, so a set of activation cells reads as
 * a list of `[column, rowsUp]` pairs; a behaviour field holding such an array is written the same way and
 * reads back unchanged.
 *
 * @param template The template.
 * @returns The file's text, two-space indented, ending in a newline.
 * @throws Error when a behaviour field is named `__proto__`.
 */
export function serialiseTemplate(template: PropTemplate): string {
  const value = { schemaVersion: TEMPLATE_SCHEMA_VERSION, name: template.name, pieces: template.pieces.map((piece, i) => plainPiece(piece, `pieces[${i}]`)) };
  return fileText(value);
}

/**
 * A file's text for plain data: two-space indented, every array of two integers on one line, ending in a
 * newline. The template and preset files are written this way.
 *
 * @param value The data.
 * @returns The text.
 */
export function fileText(value: unknown): string {
  return `${JSON.stringify(value, null, 2).replace(/\[\s+(-?\d+),\s+(-?\d+)\s+\]/g, "[$1, $2]")}\n`;
}

/**
 * Reads a template of schema 1 or 2. A file names no tile size, so nothing is converted here: a schema 1
 * piece's whole rows come back as `sortRows` beside a `sortPixels` of 0, and become pixels where the
 * template is placed (`EditorStore.selection.place`).
 *
 * @param text The file's text.
 * @returns The template.
 * @throws Error naming the path of the first problem: not JSON, another schema, a bad name, no pieces or too
 *   many, or a malformed piece.
 */
export function deserialiseTemplate(text: string): PropTemplate {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("a template file must be JSON");
  }
  const record = asRecord(parsed, "template");
  const version = asInteger(record["schemaVersion"], "template.schemaVersion");
  if (!READABLE_TEMPLATE_SCHEMAS.includes(version)) {
    throw new Error(`template.schemaVersion: templates of schema ${version} are not read by this build, which reads schemas ${READABLE_TEMPLATE_SCHEMAS.join(", ")}`);
  }
  const name = asString(record["name"], "template.name");
  const problem = templateNameProblem(name);
  if (problem !== null) {
    throw new Error(`template.name: ${problem}`);
  }
  const pieces = asArray(record["pieces"], "template.pieces");
  if (pieces.length === 0) {
    throw new Error("template.pieces must hold at least one piece");
  }
  if (pieces.length > MAX_TEMPLATE_PIECES) {
    throw new Error(`template.pieces holds at most ${MAX_TEMPLATE_PIECES} pieces, not ${pieces.length}`);
  }
  return { name, pieces: pieces.map((piece, i) => readPiece(piece, version, `template.pieces[${i}]`)) };
}

/**
 * A piece as plain data with a fixed key order. Rows no placement has converted yet are written as `sortRows`
 * after the pixels, and left out when the piece holds none.
 *
 * @param piece The piece.
 * @param path Path for error messages.
 * @returns The plain object.
 * @throws Error when a behaviour field is named `__proto__`.
 */
function plainPiece(piece: TemplatePiece, path: string): Record<string, unknown> {
  return {
    role: piece.role,
    frame: { tileset: piece.frame.tileset, tile: piece.frame.tile },
    dx: piece.dx,
    dy: piece.dy,
    flipX: piece.flipX,
    flipY: piece.flipY,
    sortPixels: piece.sortPixels,
    ...(piece.sortRows === undefined || piece.sortRows === 0 ? {} : { sortRows: piece.sortRows }),
    ...(piece.behaviour === undefined ? {} : { behaviour: plainBehaviour(piece.behaviour, `${path}.behaviour`) }),
    ...(piece.footprint === undefined ? {} : { footprint: plainInstanceFootprint(piece.footprint) }),
    ...(piece.keepPlacement === true ? { keepPlacement: true } : {}),
  };
}

/**
 * A behaviour as plain data: fixed keys, its fields, state frames and animations sorted by key.
 *
 * @param behaviour The behaviour.
 * @param path Path for error messages.
 * @returns The plain object.
 * @throws Error when a field is named `__proto__`.
 */
export function plainBehaviour(behaviour: PropBehaviour, path: string): Record<string, unknown> {
  const refs = (map: { readonly [key: string]: TileRef }): Record<string, unknown> => Object.fromEntries(Object.keys(map).sort().map((key) => [key, { tileset: map[key]?.tileset, tile: map[key]?.tile }]));
  return {
    type: behaviour.type,
    fields: sortedRecord(behaviour.fields, `${path}.fields`),
    ...(behaviour.frames === undefined ? {} : { frames: refs(behaviour.frames) }),
    ...(behaviour.animations === undefined ? {} : { animations: refs(behaviour.animations) }),
    ...(behaviour.activation === undefined ? {} : { activation: behaviour.activation.map((cell) => [cell[0], cell[1]]) }),
  };
}

/**
 * Reads a piece; a hand-written piece may leave out its flips and sort offset. A schema 1 piece's
 * `sortOffset` is whole rows and is handed back as `sortRows`; a schema 2 piece's `sortPixels` is whole
 * pixels, and it may carry the `sortRows` of a schema 1 piece nothing has placed yet. Either schema refuses
 * the other's name for the offset, so rows are never read as pixels, and schema 1 refuses `sortRows`, which
 * only schema 2 writes, so rows under that name are never read as no offset.
 *
 * @param value Candidate value.
 * @param version The file's schema version, 1 or 2.
 * @param path Path for error messages.
 * @returns The piece.
 * @throws Error naming the path when a field is malformed.
 */
function readPiece(value: unknown, version: number, path: string): TemplatePiece {
  const record = asRecord(value, path);
  const inRows = version === ROWS_SCHEMA_VERSION;
  const other = inRows ? "sortPixels" : "sortOffset";
  if (record[other] !== undefined) {
    throw new Error(`${path}.${other} is not a field of a schema ${version} template piece, whose sort offset is ${inRows ? "sortOffset, in whole rows" : "sortPixels, in whole pixels"}`);
  }
  if (inRows && record["sortRows"] !== undefined) {
    throw new Error(`${path}.sortRows is not a field of a schema 1 template piece, whose sort offset is sortOffset, in whole rows`);
  }
  const rowsKey = inRows ? "sortOffset" : "sortRows";
  const sortRows = record[rowsKey] === undefined ? 0 : asInteger(record[rowsKey], `${path}.${rowsKey}`);
  const sortPixels = inRows || record["sortPixels"] === undefined ? 0 : asInteger(record["sortPixels"], `${path}.sortPixels`);
  const role = asString(record["role"], `${path}.role`);
  if (!(TEMPLATE_ROLES as readonly string[]).includes(role)) {
    throw new Error(`${path}.role must be ${TEMPLATE_ROLES.join(", ")}, not ${JSON.stringify(role)}`);
  }
  const behaviour = record["behaviour"] === undefined ? undefined : readBehaviour(record["behaviour"], `${path}.behaviour`);
  if (behaviour !== undefined && record["footprint"] !== undefined) {
    throw new Error(`${path}.footprint: a piece with a behaviour takes no footprint of its own`);
  }
  const footprint = record["footprint"] === undefined ? undefined : readInstanceFootprint(record["footprint"], `${path}.footprint`);
  return {
    role: role as TemplateRole,
    frame: readTileRef(record["frame"], `${path}.frame`),
    dx: readOffset(record["dx"], `${path}.dx`),
    dy: readOffset(record["dy"], `${path}.dy`),
    flipX: record["flipX"] === undefined ? false : asBoolean(record["flipX"], `${path}.flipX`),
    flipY: record["flipY"] === undefined ? false : asBoolean(record["flipY"], `${path}.flipY`),
    sortPixels,
    ...(sortRows === 0 ? {} : { sortRows }),
    ...(behaviour === undefined ? {} : { behaviour }),
    ...(footprint === undefined ? {} : { footprint }),
    ...(record["keepPlacement"] !== undefined && asBoolean(record["keepPlacement"], `${path}.keepPlacement`) ? { keepPlacement: true as const } : {}),
  };
}

/**
 * Reads an offset in whole pixels.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The offset.
 * @throws Error when the value is not an integer within `MAX_OFFSET` of zero.
 */
function readOffset(value: unknown, path: string): number {
  const offset = asInteger(value, path);
  if (Math.abs(offset) > MAX_OFFSET) {
    throw new Error(`${path} must lie within ${MAX_OFFSET} pixels of the anchor`);
  }
  return offset;
}

/**
 * Reads a tile reference: a named tileset and a local tile id in range.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The reference.
 * @throws Error when the tileset is empty or the tile is out of range.
 */
export function readTileRef(value: unknown, path: string): TileRef {
  const record = asRecord(value, path);
  const tileset = asString(record["tileset"], `${path}.tileset`);
  if (tileset === "") {
    throw new Error(`${path}.tileset must name a tileset`);
  }
  return { tileset, tile: asIntegerInRange(record["tile"], MAX_TILE_ID, `${path}.tile`) };
}

/**
 * Reads tile references keyed by name.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The references.
 * @throws Error when a key is `__proto__` or a reference is malformed.
 */
function readTileRefs(value: unknown, path: string): Record<string, TileRef> {
  const record = asRecord(value, path);
  const out: Record<string, TileRef> = {};
  for (const key of Object.keys(record)) {
    if (key === FORBIDDEN_KEY) {
      throw new Error(`${path}.${FORBIDDEN_KEY} is not an allowed key`);
    }
    out[key] = readTileRef(record[key], `${path}.${key}`);
  }
  return out;
}

/**
 * Reads a behaviour: its type, fields, state frames, transition animations and activation cells.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The behaviour.
 * @throws Error naming the path when a part is malformed.
 */
export function readBehaviour(value: unknown, path: string): PropBehaviour {
  const record = asRecord(value, path);
  const type = asString(record["type"], `${path}.type`);
  if (type === "") {
    throw new Error(`${path}.type must name an object type`);
  }
  const frames = record["frames"] === undefined ? undefined : readTileRefs(record["frames"], `${path}.frames`);
  const animations = record["animations"] === undefined ? undefined : readTileRefs(record["animations"], `${path}.animations`);
  const cells = record["activation"] === undefined ? undefined : asArray(record["activation"], `${path}.activation`);
  if (cells !== undefined && cells.length > MAX_CELLS) {
    throw new Error(`${path}.activation holds at most ${MAX_CELLS} cells`);
  }
  const activation = cells?.map((cell, i) => readCell(cell, `${path}.activation[${i}]`));
  return {
    type,
    fields: asJsonRecord(record["fields"], `${path}.fields`),
    ...(frames === undefined ? {} : { frames }),
    ...(animations === undefined ? {} : { animations }),
    ...(activation === undefined ? {} : { activation }),
  };
}

/**
 * Reads one `[column, rowsUp]` cell; cells may be negative, since activation cells reach outside the frame.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The cell.
 * @throws Error when the value is not a pair of integers.
 */
function readCell(value: unknown, path: string): readonly [number, number] {
  const pair = asArray(value, path);
  if (pair.length !== 2) {
    throw new Error(`${path} must be a [column, rowsUp] pair`);
  }
  return [asInteger(pair[0], `${path}[0]`), asInteger(pair[1], `${path}[1]`)];
}
